import React, { useMemo } from 'react'
import PropTypes from 'prop-types'

import { useMakeSelector } from 'hooks/redux'
import { makeTimerConditionsSelector } from 'selectors/feature/queries'
import { makeConditionSelector } from 'selectors/feature/conditions'

import strings from 'utils/strings'
import { timerValue } from 'utils/timers'
import { timerModeDescriptions } from 'i18n/displayConstants/featureTimerModes'

import conditionLabelText from 'components/feature/utils/conditionLabelText'

import wrapAndReplace from 'components/utils/wrapAndReplace'
import {
  StyledInputLabel,
  StyledUl,
  StyledLi,
} from './TimerModeDescription.style'

const useTimerConditions = (uri) => {
  const [enableUri, resetUri] = useMakeSelector(makeTimerConditionsSelector, {
    queryUri: uri,
  })
  const enableCondition = useMakeSelector(makeConditionSelector, {
    queryUri: enableUri,
  })
  const resetCondition = useMakeSelector(makeConditionSelector, {
    queryUri: resetUri,
  })

  return [enableCondition, resetCondition]
}

// check for truthy value here, if it is false `conditionLabelText` will return '-Undefined-`, so we want to bypass that
const conditionText = (obj) =>
  obj.selectedInputDescription && conditionLabelText(obj)

const TimerModeDescription = ({ mode, className, initialValue, uri }) => {
  const [enableCondition, resetCondition] = useTimerConditions(uri)

  const stringMapper = useMemo(() => {
    const enableConditionText = conditionText(enableCondition)
    const resetConditionText = conditionText(resetCondition)
    const initialValueText = initialValue > 0 && timerValue(initialValue)

    return {
      [strings.activated]: enableConditionText,
      [strings.activate_query_label]: enableConditionText,
      [strings.reset_query_label]: resetConditionText,
      [strings.its_initial_value]: initialValueText,
    }
  }, [enableCondition, resetCondition, initialValue])

  const currentModeDescriptions = timerModeDescriptions(mode)

  const label = strings.timer_modes[mode]
  const paragraphs = useMemo(
    () =>
      currentModeDescriptions &&
      currentModeDescriptions.length > 0 &&
      currentModeDescriptions.map((paragraph) => (
        <StyledLi key={paragraph}>
          {wrapAndReplace(paragraph, stringMapper)}
        </StyledLi>
      )),
    [currentModeDescriptions, stringMapper]
  )

  return (
    <>
      <StyledInputLabel weight="bold" label={label} />

      <StyledUl className={className} size="small">
        {paragraphs}
      </StyledUl>
    </>
  )
}

TimerModeDescription.propTypes = {
  mode: PropTypes.string,
  className: PropTypes.string,
  uri: PropTypes.string,
  initialValue: PropTypes.string,
}

export default TimerModeDescription
